import React, { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import DSBox from "../../../system-design/DSBox";
import DSButton from "../../../system-design/DSButton";
import DSCard from "../../../system-design/DSCard";
import DSCardContent from "../../../system-design/DSCardContent";
import DSErrorButton from "../../../system-design/DSErrorButton";
import DSIconButton from "../../../system-design/DSIconButton";
import DSStack from "../../../system-design/DSStack";
import DSTypography from "../../../system-design/DSTypography";
import ResetDeviceDialog from "./ResetDeviceDialog";
import TransferDeviceDialog from "./TransferDeviceDialog";
import { onlineCheck, toDateSafe } from "../../../../utils/util";
import { useDevice } from "../../context/DeviceModalContext";
import RegisterDetailsModal from "../RegisterDetailsDialog";
import ClockChangeLogsDialog from "../ClockChangeLogsDialog";
import KlydoChangeLogDialog from "../KlydoChangeLogDialog";
import useClearGiftMutation from "../../hooks/useClearGiftMutation";
import useClearOrderMutation from "../../hooks/useClearOrderMutation";
import useDetachOrderMutation from "../../hooks/useDetachOrderMutation";
import DSSectionHeading from "../../../system-design/DSSectionHeading";
import OrderInfoRow from "./OrderInfoRow";
import { sectionCardSx } from "../../../system-design/sectionCardSx";

// Muted label for inline label/value rows (caption variant renders a span)
const Label = ({ children }: { children: React.ReactNode }) => (
  <DSTypography
    variant="caption"
    sx={{ color: "text.secondary", fontSize: "13px", fontWeight: 600 }}
  >
    {children}
  </DSTypography>
);

const InfoRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <DSTypography variant="body2" sx={{ color: "text.primary" }}>
    <Label>{label}:</Label> {children}
  </DSTypography>
);

const WARRANTY_YEARS = 1;
const RETURN_WINDOW_DAYS = 60;

const formatDate = (date: Date) =>
  date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });

// "Yes (until X)" while now is before the deadline, "No (ended X)" after it
const DeadlineStatus = ({ deadline }: { deadline: Date }) => {
  const active = Date.now() < deadline.getTime();
  return (
    // caption renders a span, so it nests inside the InfoRow paragraph
    <DSTypography
      variant="caption"
      sx={{
        color: active ? "success.main" : "error.main",
        fontSize: "inherit",
      }}
    >
      {active ? "Yes" : "No"} ({active ? "until" : "ended"}{" "}
      {formatDate(deadline)})
    </DSTypography>
  );
};

const DeviceInfoCard = () => {
  const { device, removeRegister, resetDeviceData } = useDevice();
  const { mutateAsync: clearGift } = useClearGiftMutation(device.id);
  const { mutateAsync: clearOrder } = useClearOrderMutation(device.id);
  const { mutateAsync: detachOrder } = useDetachOrderMutation(device.id);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [showClockChangeLogsDialog, setShowClockChangeLogsDialog] =
    useState(false);
  const [showKlydoChangeLogDialog, setShowKlydoChangeLogDialog] =
    useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  const heartbeatDate = useMemo(
    () => new Date(device.heartbeat),
    [device.heartbeat],
  );
  const isOnline = onlineCheck(heartbeatDate);
  const remoteBatteryDate = useMemo(
    () => toDateSafe(device.remoteBattery?.time),
    [device.remoteBattery?.time],
  );
  const { warrantyEnd, returnEnd } = useMemo(() => {
    const registeredAt = toDateSafe(device.registerProduct);
    if (!registeredAt) return {};
    const warranty = new Date(registeredAt);
    warranty.setFullYear(warranty.getFullYear() + WARRANTY_YEARS);
    const ret = new Date(registeredAt);
    ret.setDate(ret.getDate() + RETURN_WINDOW_DAYS);
    return { warrantyEnd: warranty, returnEnd: ret };
  }, [device.registerProduct]);

  return (
    <DSBox sx={{ mb: 3 }}>
      <DSCard className="card-stats" sx={sectionCardSx}>
        <DSSectionHeading>Info</DSSectionHeading>
        <DSCardContent>
          <DSStack spacing={1} sx={{ ml: 2.5 }}>
            <InfoRow label="Device ID">{device.id}</InfoRow>

            {device.serial && <InfoRow label="Serial">{device.serial}</InfoRow>}

            <InfoRow label="Friendly ID">{device.idf}</InfoRow>

            <DSBox>
              <DSTypography variant="body2">
                <Label>Orders:</Label>
              </DSTypography>
              <DSStack spacing={0.5} sx={{ ml: 1, mt: 0.5 }}>
                {device.orders?.length ? (
                  device.orders.map((order) => (
                    <OrderInfoRow
                      key={order}
                      orderName={order}
                      onDetach={detachOrder}
                    />
                  ))
                ) : (
                  <DSTypography
                    variant="body2"
                    sx={{ color: "text.secondary" }}
                  >
                    No orders
                  </DSTypography>
                )}
              </DSStack>
            </DSBox>

            <DSTypography variant="body2">
              <Label>Version: </Label>
              <NavLink
                to={`/admin/versions?version=${device?.version?.toString() || ""}`}
              >
                {device.version}
              </NavLink>
            </DSTypography>

            <InfoRow label="Firmware">{device.firmware_version}</InfoRow>

            <DSBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <DSTypography variant="body2">
                <Label>Heartbeat:</Label>
              </DSTypography>
              <FiberManualRecordIcon
                sx={{ fontSize: 12 }}
                color={isOnline ? "success" : "error"}
              />
              <DSTypography variant="body2">
                {isOnline ? "Online" : "Offline"} -{" "}
                {heartbeatDate.toLocaleString(undefined, {
                  year: "numeric",
                  month: "numeric",
                  day: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  second: "numeric",
                })}
              </DSTypography>
            </DSBox>

            <InfoRow label="Remote Battery">
              {device.remoteBattery
                ? `${device.remoteBattery.value}%${
                    remoteBatteryDate
                      ? ` - ${remoteBatteryDate.toLocaleString(undefined, {
                          year: "numeric",
                          month: "numeric",
                          day: "numeric",
                          hour: "numeric",
                          minute: "numeric",
                        })}`
                      : ""
                  }`
                : "No data"}
            </InfoRow>

            <DSBox sx={{ display: "flex", alignItems: "center" }}>
              {device.registerProduct ? (
                <>
                  <DSTypography variant="body2">
                    <Label>Registered at:</Label>{" "}
                    {device.registerProduct?.toLocaleString()}
                  </DSTypography>
                  <DSIconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeRegister();
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </DSIconButton>
                </>
              ) : (
                <>
                  <DSTypography variant="body2">
                    <Label>Not registered:</Label>
                  </DSTypography>
                  <DSButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowRegisterDialog(true);
                    }}
                  >
                    Register device
                  </DSButton>
                </>
              )}
            </DSBox>

            {warrantyEnd && returnEnd && (
              <>
                <InfoRow label="In warranty">
                  <DeadlineStatus deadline={warrantyEnd} />
                </InfoRow>
                <InfoRow label="Can return">
                  <DeadlineStatus deadline={returnEnd} />
                </InfoRow>
              </>
            )}

            {device.user?.value && (
              <InfoRow label="User">{device.user.value}</InfoRow>
            )}

            {device.location && (
              <InfoRow label="Location">
                {[
                  device.location.city,
                  device.location.region,
                  device.location.country_code,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </InfoRow>
            )}

            {device.location?.timezone && (
              <InfoRow label="Timezone">{device.location.timezone}</InfoRow>
            )}

            {device.mode?.value && (
              <InfoRow label="Mode">{device.mode.value}</InfoRow>
            )}

            {device.clientDetails && (
              <DSBox
                sx={{
                  borderRadius: "12px",
                  borderStyle: "solid",
                  borderWidth: 1,
                  borderColor: "divider",
                  p: 1.5,
                }}
              >
                <DSBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <DSTypography variant="body2">
                    <Label>Registration form data:</Label>
                  </DSTypography>
                  <DSIconButton
                    size="small"
                    onClick={() => setShowRegisterDialog(true)}
                  >
                    <EditIcon fontSize="small" />
                  </DSIconButton>
                </DSBox>
                {Object.entries(device.clientDetails).map(
                  ([key, value]) =>
                    value && (
                      <DSTypography key={key} variant="body2">
                        <Label>{key}:</Label> {value}
                      </DSTypography>
                    ),
                )}
              </DSBox>
            )}

            <InfoRow label="Auto created user">
              {device.autoCreate?.toString()}
            </InfoRow>

            <DSBox sx={{ display: "flex", gap: 1, mt: 1 }}>
              <DSButton onClick={() => setShowClockChangeLogsDialog(true)}>
                Clock Change Logs
              </DSButton>
              <DSButton onClick={() => setShowKlydoChangeLogDialog(true)}>
                Klydo Changelog
              </DSButton>
              <DSErrorButton onClick={() => setShowResetModal(true)}>
                Reset device cloud data
              </DSErrorButton>
              <DSButton onClick={() => setShowTransferModal(true)}>
                Transfer data
              </DSButton>
            </DSBox>
          </DSStack>
        </DSCardContent>
      </DSCard>

      <RegisterDetailsModal
        open={showRegisterDialog}
        onClose={() => setShowRegisterDialog(false)}
        device={device}
      />
      <ClockChangeLogsDialog
        open={showClockChangeLogsDialog}
        onClose={() => setShowClockChangeLogsDialog(false)}
        clockId={device.id}
        clockIdf={device.idf}
      />
      <KlydoChangeLogDialog
        open={showKlydoChangeLogDialog}
        onClose={() => setShowKlydoChangeLogDialog(false)}
        clockId={device.id}
        clockIdf={device.idf}
      />
      <ResetDeviceDialog
        open={showResetModal}
        onClose={() => setShowResetModal(false)}
        deviceIdf={device.idf}
        resetDeviceData={resetDeviceData}
        clearGift={clearGift}
        clearOrder={clearOrder}
      />
      <TransferDeviceDialog
        open={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        sourceClockId={device.id}
        sourceIdf={device.idf}
      />
    </DSBox>
  );
};

export default DeviceInfoCard;
